'use client';

import { useGetDefensives, useGetFertilizers } from '@practium/features/api';
import { useCallback, useMemo } from 'react';

export interface ProductForm {
	productClass: string;
	productCommercialName: string;
	productTradeMark: string;
}

const uniqueOptions = (options: { value: string; label: string }[]) => {
	const map = new Map<string, { value: string; label: string }>();

	options.forEach((option) => {
		if (option.value && option.label) {
			map.set(option.value, option);
		}
	});

	return Array.from(map.values());
};

export const useProductsOptions = () => {
	const { data: fertilizers = [] } = useGetFertilizers();
	const { data: defensives = [] } = useGetDefensives();

	const products = useMemo(() => [...fertilizers, ...defensives], [fertilizers, defensives]);

	const classProductOptions = useMemo(
		() =>
			uniqueOptions(
				products.flatMap((product: any) => {
					const value = product.category ?? product.class ?? '';
					const classes: string[] = Array.isArray(value) ? value : [value];
					return classes.map(value => ({ value, label: value }));
				})
			),
		[products]
	);

	const getProductOptions = useCallback(
		(product: ProductForm) => {
			const filteredProducts = product.productClass
				? products.filter((item: any) => {
						const productClass = item.category ?? item.class;
						return Array.isArray(productClass)
							? productClass.includes(product.productClass)
							: productClass === product.productClass;
					})
				: products;

			const productCommercialNameOptions = uniqueOptions(
				filteredProducts.map((item: any) => ({
					value: item.name ?? item.commercialName ?? '',
					label: item.name ?? item.commercialName ?? '',
				}))
			);

			const filteredProductsByName = product.productCommercialName
				? filteredProducts.filter((item: any) => {
						const productName = item.name ?? item.commercialName;
						return productName === product.productCommercialName;
					})
				: filteredProducts;

			const productTradeMarkOptions = uniqueOptions(
				filteredProductsByName.map((item: any) => ({
					value: item.manufacturer?.tradingName ?? item.manufacturer?.businessName ?? '',
					label: item.manufacturer?.tradingName ?? item.manufacturer?.businessName ?? '',
				}))
			);

			return {
				productCommercialNameOptions,
				productTradeMarkOptions,
			};
		},
		[products]
	);

	return {
		classProductOptions,
		getProductOptions,
	};
};
